import { Application } from '@platform/client';
import { LumenApplicationParameters } from '@platform/client/schemas';
import { ErrorBox, FormsCheckBox, FormsInputDropdown } from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Aanu-Gopald.svg';

type FormValues = {
  managerSupport: boolean;
  referralMethod: string;
};

const LUMEN_REFERRAL_OPTIONS = [
  { text: 'A colleague within your company', textValue: 'colleague' },
  { text: 'Your manager', textValue: 'manager' },
  { text: 'Your Global CSR leader', textValue: 'globalCSR' },
  { text: 'Your Regional CSR leader', textValue: 'regionalCSR' },
  { text: 'Email communication from your company', textValue: 'email' },
  { text: 'Internal meeting or town hall', textValue: 'meeting' },
  { text: 'LinkedIn', textValue: 'linkedin' },
  { text: 'Other', textValue: 'other' },
];

export const LumenSubmission = (props: ApplicationPageProps) => {
  const { onBack, progress } = { ...props };
  const [submitted, setSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);
  const { getApplicationData } = useApplicationLocalStorage<LumenApplicationParameters>();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormValues>({ mode: 'onBlur', reValidateMode: 'onChange' });

  const submitLumenMutation = Application.useSubmitLumenApplication();
  const queryClient = useQueryClient();

  const submit: SubmitHandler<FormValues> = (data) => {
    if (data.managerSupport && data.referralMethod) {
      submitLumenMutation.mutate(
        {
          data: {
            ...getApplicationData(),
            referralMethod: data.referralMethod,
          },
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({
              queryKey: Application.getGetProfessionalApplicationStatusQueryKey(),
            });
          },
          onError: () => {
            setFailed(true);
          },
        },
      );
    } else {
      setSubmitted(true);
    }
  };

  return (
    <ApplicationBackground
      progress={progress}
      onBack={onBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
    >
      <ApplicationPageContent
        title='Ready to submit? Just one more thing.'
        description='After you submit, your responses are final, so take a moment to make sure everything is just right.'
      >
        <FormsInputDropdown
          control={control}
          name='referralMethod'
          label='How did you find out about this opportunity?'
          placeholder='Select one'
          items={LUMEN_REFERRAL_OPTIONS}
          type='default'
          error={errors.referralMethod}
          inputDisabled={true}
        />
        <FormsCheckBox
          control={control}
          name='managerSupport'
          size='md'
          description={
            <div>Yes, I've talked with my manager and have their support to participate.</div>
          }
        />
        {submitted && (
          <ErrorBox showAlertTriangle={true}>
            Agree to the program requirements checkbox to continue
          </ErrorBox>
        )}
        {failed && (
          <ErrorBox showAlertTriangle={true}>
            There was an error submitting your application. Please try again later.
          </ErrorBox>
        )}
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
